import { Typography, Divider, Button } from 'antd';
import aboutImage from '../../images/CA-WILDFIRE-COMMONS_v2.png';

// import WildfireDiagram from './WildfireDiagram'; // The React Flow component we made
const { Title, Paragraph } = Typography;
const AboutPage = () => {
  const brandColors = {
    pine: '#2C3E37',
    sage: '#A7BFB5',
    teal: '#3F7C85',
    mist: '#F4F7F6',
    charcoal: '#2B2B2B',
  };
  return (
    <>
      <div
        id="data_collection"
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '-webkit-fill-available',
          backgroundColor: '#F8FAF9',
        }}
      >
        <div
          style={{
            backgroundSize: 'cover',
          }}
        >
          <div
            style={{
              margin: '30px 10%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <h1
              style={{
                color: '#2C3E37',
                fontWeight: 'bolder',
                fontSize: '36px',
              }}
            >
              About
            </h1>
          </div>
        </div>
        <div
          style={{
            backgroundColor: '#CEDBD6',
            backgroundSize: 'cover',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            padding: '35px',
          }}
        >
          <Paragraph style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
            The California Wildfire Commons is built as a node of the nation’s
            Wildfire Science and Technology Commons, hosted by the San Diego
            Supercomputer Center at UC San Diego. This effort reflects the
            University of California’s mission to advance knowledge and share
            its benefits with all communities across the state.
          </Paragraph>
          <Paragraph style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
            The Wildfire Science and Technology Commons offers California a rare
            opportunity: an open-science platform built on principles of
            transparency, shared access, and collaborative development.
            Supported by federal funding through the National Institute of
            Standards and Technology, the collaboration draws strength from a
            broad coalition of universities, NGOs, and research institutions
            committed to making wildfire data and tools publicly accessible.
            This foundation aligns the data systems, treatment tracking, and
            science-policy communication that a broad group of California
            partners depend on with this national infrastructure.
          </Paragraph>
          <Paragraph style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
            The California Wildfire Commons is emerging as California’s unified
            platform to catalog, find, view, and use data and tools about
            wildfire risk, treatments, fuels, weather, measurements, fire
            behavior, and outcomes. Grounded in open-science principles, it
            federates datasets, models, and tools so that existing investments
            become discoverable, interoperable, and available to all
            stakeholders. The Commons organizes information needs around four
            core functions (Figure 1):
          </Paragraph>
        </div>
        <div
          style={{
            backgroundColor: '#CEDBD6',
            backgroundSize: 'cover',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <img
            src={aboutImage}
            // Scales down with the page instead of forcing a 1000px+ width.
            style={{
              borderRadius: '5px',
              height: 'auto',
              maxHeight: '500px',
              maxWidth: '100%',
              objectFit: 'contain',
            }}
            alt="Organization and workflow diagram for the California Wildfire Commons, showing existing capabilities"
          />
          <Paragraph style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
            Figure 1: Organization and workflow for the Commons. Existing
            capabilities are highlighted.
          </Paragraph>
          <br />
        </div>
        <div
          style={{
            backgroundColor: '#CEDBD6',
            backgroundSize: 'cover',
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            padding: '35px',
          }}
        >
          {/* Workflow Section */}
          <Title level={2} style={{ color: brandColors.pine }}>
            Current Capabilities
          </Title>
          <Paragraph style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
            The Commons has already brought together several important tools and
            datasets that are active and accessible now. Those include:
          </Paragraph>
          <Paragraph style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
            <ul>
              <li>
                Interagency Treatment Tracker: Back-end database and map service
                (Steward)
              </li>
              <li>
                Outcome repositories: Wildfire incidents and outcomes (Steward)
              </li>
              <li>
                California Landscape Metrics: Statewide landscape condition and
                risk layers (Steward)
              </li>
              <li>
                Interagency Treatment Tracker Dashboard: Public view of
                completed treatments (Report)
              </li>
              <li>
                Planscape: Decision-support platform for wildfire mitigation
                planning and evaluation (Report)
              </li>
            </ul>
          </Paragraph>
          <Title level={2} style={{ color: brandColors.pine }}>
            Framework
          </Title>
          <Paragraph style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
            The Measure-{'>'}Steward-{'>'}Share-{'>'}Report framework ensures
            that every dataset has a clear path from collection to use, with
            defined stewardship responsibilities and quality standards at each
            step.
          </Paragraph>
          <Paragraph style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
            <ul>
              <li>
                <strong>Measure.</strong> Data originates from diverse sources
                across the state: local parcel and community data, completed
                treatments reported by all ownerships, planned treatments in
                five-year programs of work, wildfire incident records, and
                landscape observations from remote sensing and field inventory.
                The Commons does not replace these collection efforts but
                connects them.
              </li>
              <li>
                <strong>Steward.</strong> Partner systems maintain authoritative
                datasets within their domains. The Interagency Treatment Tracker
                manages completed treatment records. The WUI Commons aggregates
                community and parcel-level mitigation data. Outcome repositories
                compile incident and post-fire information from CAL FIRE, Cal
                OES, USFS, and others. California Landscape Metrics and{' '}
                <Button
                  type="link"
                  style={{ padding: 0, fontSize: '1.1rem', color: '#664A00' }}
                  onClick={() => {
                    window.open(
                      'https://www.nasa.gov/werk/',
                      '_blank',
                      'noopener,noreferrer'
                    );
                  }}
                >
                  <strong>WERK</strong>
                </Button>{' '}
                provide statewide vegetation and risk layers. Each steward
                applies quality controls and updates on defined schedules,
                ensuring data integrity at the source.
              </li>
              <li>
                <strong>Share.</strong> The Commons federates these stewarded
                datasets into a unified hub with consistent metadata, reusable
                and extensible APIs, and standardized access. Users discover
                data through a central catalog rather than navigating multiple
                agency portals. The Commons handles versioning, documentation,
                and access controls so that partners can connect once and trust
                that data flows reliably.
              </li>
              <li>
                <strong>Report.</strong> Dashboards and applications transform
                shared data into actionable information. The Interagency
                Treatment Dashboard displays completed work. Planscape supports
                planning and evaluation of future treatments. Outcome reporting
                tracks progress toward Action Plan goals. Third-party tools
                connect via APIs to build additional analyses. Each reporting
                application draws from the same trusted data, ensuring
                consistency across platforms.
              </li>
            </ul>
          </Paragraph>{' '}
          <Title level={2} style={{ color: brandColors.pine }}>
            3-Year Plan
          </Title>
          <Paragraph style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
            Planners, practitioners, and decision-makers will use dashboards to
            explore maps and download ready-made summaries, with APIs available
            for advanced applications. Users can trust that data are
            quality-checked and documented. As the consortium of contributors
            and users grows and identifies additional needs, the California
            Wildfire Commons will power new dashboards and planning tools, all
            grounded in the Measure-{'>'}Steward-{'>'}Share-{'>'}Report
            framework that ensures accountability and interoperability across
            the system. To support this vision, additional capabilities will be
            built out, including:
          </Paragraph>
          <Paragraph style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
            <ul>
              <li>
                <strong>Five-Year Programs of Work:</strong> A clear, statewide
                picture of planned treatments across all ownerships. Planned
                treatments are a priority information need. (Measure → Steward)
              </li>
              <li>
                <strong>Community Mitigation Tracker:</strong> Secure reporting
                and roll-ups for completed parcel and community mitigations,
                including defensible space inspections and home hardening
                actions. (Report)
              </li>
              <li>
                <strong>California Community Metrics:</strong> A companion to
                California Landscape Metrics that summarizes community
                conditions and risk using community-scale observations and
                models. (Measure → Steward)
              </li>
              <li>
                <strong>Outcome-Based Reporting:</strong> Build the capacity in
                Planscape to track progress toward targets, desired conditions,
                and outcomes. (Report)
              </li>
              <li>
                <strong>APIs and Data Services:</strong> Stable endpoints and
                documentation so partners and third-party tools can easily
                connect to key data. (Share)
              </li>
            </ul>
          </Paragraph>
        </div>
      </div>
    </>
  );
};
export default AboutPage;
